import { css } from "@linaria/core";
import classNames from "classnames";
import type { SVGProps } from "react";
import React from "react";
import { color, defaultButtonIconSize, radius, fonts } from "..";
import { shadows } from "../Theme/shadows";

export type Variant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "destructive"
  | "ghost"
  | "link"
  | "destructiveLink";

const button = css`
  font: ${fonts.body1};
  color: currentColor;
  background-color: transparent;
  border: 1px solid transparent;
  border-radius: ${radius};
  padding: 8px 20px;
  white-space: nowrap;
  user-select: none;
  text-decoration: none;
  height: 40px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  transition:
    box-shadow 144ms linear,
    background-color 72ms linear,
    color 72ms linear;

  /* .disabled for anchors, :disabled for real buttons */
  &.disabled,
  :disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const shadow = css`
  :not(.disabled):not(:disabled) {
    box-shadow: ${shadows.shadow100};
  }
`;

export const hoverShadow = css`
  :not(.disabled):not(:disabled) {
    :hover,
    :active {
      box-shadow: ${shadows.shadow200};
    }
  }
`;

export const primaryButton = css`
  color: ${color.hydrogen};
  background-color: ${color.lightNitrogen500_Main};

  :not(.disabled):not(:disabled) {
    :hover {
      background-color: ${color.lightNitrogen600};
    }
    :active {
      background-color: ${color.lightNitrogen700};
    }
  }
`;

const secondaryButton = css`
  color: ${color.lightNitrogen500_Main};
  border: 1px solid;
  border-color: ${color.lightNitrogen500_Main};
  background-color: ${color.hydrogen};

  :not(.disabled):not(:disabled) {
    :hover {
      color: ${color.hydrogen};
      background-color: ${color.lightNitrogen600};
      border-color: ${color.lightNitrogen600};
    }
    :active {
      color: ${color.hydrogen};
      border-color: ${color.lightNitrogen600};
      background-color: ${color.lightNitrogen700};
    }
  }
`;

const tertiaryButton = css`
  color: ${color.carbon800};
  border: 1px solid;
  border-color: ${color.titanium200};
  background-color: ${color.hydrogen};

  :not(.disabled):not(:disabled) {
    :hover {
      background-color: ${color.titanium100};
    }

    :active {
      background-color: ${color.titanium200};
    }
  }
`;

const ghostButton = css`
  border: none;
  color: ${color.carbon800};

  :not(.disabled):not(:disabled) {
    :hover {
      background-color: ${color.titanium100};
    }

    :active {
      background-color: ${color.titanium200};
    }
  }
`;

const linkButton = css`
  padding: 0;
  border: none;
  color: ${color.lightNitrogen500_Main};
  align-items: center;
  line-height: normal;
  height: 20px;

  :not(.disabled):not(:disabled) {
    :hover,
    :active {
      text-decoration: underline;
    }

    :hover {
      color: ${color.lightNitrogen600};
    }

    :active {
      color: ${color.lightNitrogen700};
    }
  }
`;

const linkDestructiveButton = css`
  padding: 0;
  border: none;
  color: ${color.oxygen400};
  align-items: center;
  line-height: normal;
  height: 20px;

  :not(.disabled):not(:disabled) {
    :hover,
    :active {
      text-decoration: underline;
    }

    :hover {
      color: ${color.oxygen600};
    }

    :active {
      color: ${color.oxygen700};
    }
  }
`;

const destructiveButton = css`
  color: ${color.hydrogen};
  background-color: ${color.oxygen400};

  :not(.disabled):not(:disabled) {
    :hover {
      background-color: ${color.oxygen500};
    }
    :active {
      background-color: ${color.oxygen600};
    }
  }
`;

const slimButton = css`
  :not(.${linkButton}) {
    height: 32px;
  }
  :not(.${linkDestructiveButton}) {
    height: 32px;
  }

  &.${linkButton} {
    font: ${fonts.body2};
  }
  &.${linkDestructiveButton} {
    font: ${fonts.body2};
  }
`;

const buttonIcon = css`
  display: inline-flex;
  margin-right: 4px;

  & > svg {
    width: ${defaultButtonIconSize}px;
    height: ${defaultButtonIconSize}px;
  }
`;

const buttonContent = css`
  display: flex;
  align-items: center;
`;

interface P extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  slim?: boolean;
  dark?: boolean;
  leftIcon?: React.ReactElement<SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
}

function Button(
  { className, variant, dark, type, slim, leftIcon, children, ...props }: P,
  forwardedRef: React.ForwardedRef<HTMLButtonElement>,
) {
  return (
    <button
      ref={forwardedRef}
      type={type || (props.onClick ? "button" : "submit")}
      className={classNames(className, button, {
        dark,
        [shadow]:
          Boolean(variant) &&
          variant !== "ghost" &&
          variant !== "link" &&
          variant !== "destructiveLink" &&
          variant !== "secondary" &&
          variant !== "tertiary",
        [hoverShadow]:
          Boolean(variant) &&
          (variant === "primary" || variant === "secondary" || variant === "destructive"),
        [primaryButton]: variant === "primary",
        [secondaryButton]: variant === "secondary",
        [tertiaryButton]: variant === "tertiary",
        [ghostButton]: variant === "ghost",
        [linkButton]: variant === "link",
        [linkDestructiveButton]: variant === "destructiveLink",
        [destructiveButton]: variant === "destructive",
        [slimButton]: slim,
      })}
      {...props}
    >
      <span className={buttonContent}>
        {leftIcon === undefined ? null : <span className={buttonIcon}>{leftIcon}</span>}
        {children}
      </span>
    </button>
  );
}

export default React.memo(React.forwardRef<HTMLButtonElement, P>(Button));
